import type { ActivityCommentScope } from '@ayocore/activity/common/types/activity-comment-scope';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';
import type { ActivityChatPin } from '@ayocore/activity/common/types/activity-pin';
import type { ActivityComposerInsertFragment } from '../types/activity-composer-fragment';
import {
  DEFAULT_ACTIVITY_THREAD_QUICK_FILTER_COUNTERS,
  type ActivityThreadQuickFilter,
  type ActivityThreadQuickFilterCounters,
} from '@ayocore/activity/common/types/activity-thread-quick-filter';
import type {
  ActivityArchiveSummary,
  ActivityFeedItem,
} from '@ayocore/activity/server/repositories/ActivityThreadRepository';
import type { ActivityContext } from '../types/activity-context';

export type ActivityReplyTarget = {
  commentUuid: string;
  preview: string;
};

export type ActivityEditCommentState = {
  showEditComment: boolean;
  editingCommentUuid: string | null;
  editingCommentText: string;
  isSavingComment: boolean;
};

export type ActivityThreadsState = {
  items: ActivityFeedItem[];
  archiveSummary: ActivityArchiveSummary;
  isArchiveView: boolean;
  quickFilter: ActivityThreadQuickFilter;
  quickFilterCounters: ActivityThreadQuickFilterCounters;
  loading: boolean;
  errorMessage: string | null;
  busyThreadId: number | null;
};

export type ActivityChatState = {
  chatItems: ActivityChatItem[];
  commentsHasMoreOlder: boolean;
  loadingOlderComments: boolean;
  commentsLoading: boolean;
  commentsErrorMessage: string | null;
  eventsLoading: boolean;
  eventsErrorMessage: string | null;
  sendingComment: boolean;
  replyTarget: ActivityReplyTarget | null;
  jumpToCommentUuid: string | null;
  scopePreset: ActivityCommentScope | null;
  activeContext: ActivityContext | null;
  editComment: ActivityEditCommentState;
};

export type ActivityPinsState = {
  pins: ActivityChatPin[];
  pinsLoading: boolean;
  pinsErrorMessage: string | null;
};

export type ActivityComposerState = {
  selectedScope: ActivityCommentScope | null;
  isScopeBoundToDraft: boolean;
  focusRequestSeq: number;
  pendingInsertFragment: {
    requestId: number;
    fragment: ActivityComposerInsertFragment;
  } | null;
};

export type ActivityRootState = {
  selectedThreadId: number | null;
  selectedThread: ActivityFeedItem | null;
};

export const DEFAULT_ACTIVITY_ARCHIVE_SUMMARY: ActivityArchiveSummary = {
  archived_threads_count: 0,
  unread_count: 0,
  attention_unread_count: 0,
  my_overdue_count: 0,
  overdue_count: 0,
};

export const INITIAL_THREADS_STATE: ActivityThreadsState = {
  items: [],
  archiveSummary: { ...DEFAULT_ACTIVITY_ARCHIVE_SUMMARY },
  isArchiveView: false,
  quickFilter: 'all',
  quickFilterCounters: { ...DEFAULT_ACTIVITY_THREAD_QUICK_FILTER_COUNTERS },
  loading: true,
  errorMessage: null,
  busyThreadId: null,
};

export const INITIAL_CHAT_STATE: ActivityChatState = {
  chatItems: [],
  commentsHasMoreOlder: false,
  loadingOlderComments: false,
  commentsLoading: false,
  commentsErrorMessage: null,
  eventsLoading: false,
  eventsErrorMessage: null,
  sendingComment: false,
  replyTarget: null,
  jumpToCommentUuid: null,
  scopePreset: null,
  activeContext: null,
  editComment: {
    showEditComment: false,
    editingCommentUuid: null,
    editingCommentText: '',
    isSavingComment: false,
  },
};

export const INITIAL_PINS_STATE: ActivityPinsState = {
  pins: [],
  pinsLoading: false,
  pinsErrorMessage: null,
};

export const INITIAL_COMPOSER_STATE: ActivityComposerState = {
  selectedScope: null,
  isScopeBoundToDraft: false,
  focusRequestSeq: 0,
  pendingInsertFragment: null,
};

export const INITIAL_ROOT_STATE: ActivityRootState = {
  selectedThreadId: null,
  selectedThread: null,
};
